import { api } from '@/lib/api'
import type {
  AddressDto,
  CreateCustomerRequest,
  Customer,
  CustomerListResponse,
  EmailDto,
  PhoneDto,
  UpdateCustomerCoreRequest,
} from './types'
import { customerDisplayName } from './types'

export type CustomerPickerOption = {
  id: number
  /** "Jan Nowak" lub "ACME Sp. z o.o." */
  displayName: string
  /** "NIP 1234567890 · tel +48..." lub "tel +48..." */
  secondaryInfo?: string
}

const BASE = '/api/customers'

export async function listCustomers(offset = 0, limit = 50): Promise<CustomerListResponse> {
  const res = await api.get<CustomerListResponse>(BASE, { params: { offset, limit } })
  return res.data
}

export async function getCustomer(id: number): Promise<Customer> {
  const res = await api.get<Customer>(`${BASE}/${id}`)
  return res.data
}

export async function createCustomer(payload: CreateCustomerRequest): Promise<Customer> {
  const res = await api.post<Customer>(BASE, payload)
  return res.data
}

export async function updateCustomerCore(
  id: number,
  payload: UpdateCustomerCoreRequest,
): Promise<Customer> {
  const res = await api.patch<Customer>(`${BASE}/${id}/core`, payload)
  return res.data
}

export async function deactivateCustomer(id: number): Promise<void> {
  await api.delete(`${BASE}/${id}`)
}

export async function addPhone(customerId: number, phone: PhoneDto): Promise<PhoneDto> {
  const res = await api.post<PhoneDto>(`${BASE}/${customerId}/phones`, phone)
  return res.data
}

export async function updatePhone(
  customerId: number,
  phoneId: number,
  phone: PhoneDto,
): Promise<PhoneDto> {
  const res = await api.patch<PhoneDto>(`${BASE}/${customerId}/phones/${phoneId}`, phone)
  return res.data
}

export async function deactivatePhone(customerId: number, phoneId: number): Promise<void> {
  await api.delete(`${BASE}/${customerId}/phones/${phoneId}`)
}

export async function addEmail(customerId: number, email: EmailDto): Promise<EmailDto> {
  const res = await api.post<EmailDto>(`${BASE}/${customerId}/emails`, email)
  return res.data
}

export async function updateEmail(
  customerId: number,
  emailId: number,
  email: EmailDto,
): Promise<EmailDto> {
  const res = await api.patch<EmailDto>(`${BASE}/${customerId}/emails/${emailId}`, email)
  return res.data
}

export async function deactivateEmail(customerId: number, emailId: number): Promise<void> {
  await api.delete(`${BASE}/${customerId}/emails/${emailId}`)
}

export async function addAddress(customerId: number, address: AddressDto): Promise<AddressDto> {
  const res = await api.post<AddressDto>(`${BASE}/${customerId}/addresses`, address)
  return res.data
}

export async function updateAddress(
  customerId: number,
  addressId: number,
  address: AddressDto,
): Promise<AddressDto> {
  const res = await api.patch<AddressDto>(`${BASE}/${customerId}/addresses/${addressId}`, address)
  return res.data
}

export async function deactivateAddress(customerId: number, addressId: number): Promise<void> {
  await api.delete(`${BASE}/${customerId}/addresses/${addressId}`)
}

/**
 * Wyszukuje klientow po frazie (ILIKE: imie, nazwisko, firma, NIP, telefon, email).
 * Zwraca max 50 wynikow. Pusty/blank query zwraca pusta tablice bez uderzania w API.
 */
export async function searchCustomers(query: string): Promise<CustomerPickerOption[]> {
  if (!query.trim()) return []
  const res = await api.get<CustomerListResponse>(BASE, { params: { search: query, limit: 50 } })
  return res.data.items.map((c) => {
    const parts: string[] = []
    if (c.nip) parts.push(`NIP ${c.nip}`)
    const phone = c.phones?.find((p) => p.isActive)?.value
    if (phone) parts.push(`tel ${phone}`)
    const email = c.emails?.find((e) => e.isActive)?.value
    if (email) parts.push(email)
    return {
      id: c.id,
      displayName: customerDisplayName(c),
      secondaryInfo: parts.length > 0 ? parts.join(' · ') : undefined,
    }
  })
}
